/**
 * Construcción de la `url` que se envía a Umami (T7 · KAI-94).
 *
 * Existe por dos razones, y conviene entender las dos antes de tocarlo.
 *
 * 1. **El tracker no la actualiza solo.** Con `data-auto-track="false"` —que es
 *    lo que nos deja decidir qué se mide y qué no— Umami captura la URL UNA
 *    vez, al cargar el script, y no vuelve a mirarla: el handler que la
 *    refresca en cada `pushState` sólo se instala cuando el auto-track está
 *    activo. Si no la mandamos nosotros, toda la navegación de cliente se
 *    registra bajo la página de entrada y el embudo de T7 no mide nada.
 *
 * 2. **La query no puede viajar entera.** La frontera de `paths.js` filtra
 *    rutas, no query strings. `apps/web` no tiene `not-found.js`, así que un
 *    404 se pinta dentro del layout raíz y llega hasta aquí con su query
 *    intacta: una URL mal cortada por un cliente de correo
 *    (`/api/auth/auto-login?token=…`, token de un solo uso) acabaría en la
 *    base de datos de analítica. Por eso la query va por allowlist: entra lo
 *    que sirve para atribuir campañas y nada más. El hash se descarta siempre.
 */

/** `utm_*` (campañas) y `c` (código de tarjeta del shortener). Nada más. */
export function isAllowedQueryParam(name) {
  return name === "c" || name.startsWith("utm_");
}

/**
 * URL absoluta para el payload de Umami, con la query filtrada y sin hash.
 *
 * Absoluta y no relativa a propósito: es la forma que el propio tracker
 * produce (normaliza con `new URL(href)`), así que lo que mandamos es
 * indistinguible de lo que mandaría él.
 *
 * @param {string} origin p.ej. `https://kaizenfeedback.com`.
 * @param {string} pathname ruta ya validada por `isTrackablePath`.
 * @param {string} search query string cruda, con o sin `?`.
 */
export function buildAnalyticsUrl(origin, pathname, search) {
  const kept = new URLSearchParams();
  for (const [name, value] of new URLSearchParams(search || "")) {
    if (isAllowedQueryParam(name)) kept.append(name, value);
  }
  const query = kept.toString();
  return `${origin}${pathname}${query ? `?${query}` : ""}`;
}
